import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  Fragment
} from "react";
import PropTypes from "prop-types";
import { useRouteMatch, useHistory } from "react-router-dom";
import { i18nMark } from "@lingui/react";
import { Trans, t } from "@lingui/macro";

import { i18n } from "base/i18n";
import { useStore, Store } from "base/basestore";
import { LazyLoadComponent } from "widgets/lazyloader";
import { ErrorBoundary } from "widgets/layouts";
import { useAccountStore, useUserStore } from "base/stores";
import { debounce } from "base/utils";
import { Filter } from "kfcomponents/filter";
import { Icon } from "widgets/datadisplay";
import { Toast } from "widgets/feedback";
import { SecondaryTabs, SecondaryTab, SECONDARY_TAB_SIZES } from "widgets/tab";

import { ProcessService } from "../services/process.service.js";
import { createGridDataStore } from "../common/stores/grid.data";
import { useProcessStore } from "../common/stores/process";
import { useBulkActionsStore } from "../common/stores/bulk.actions";
import { Grid, ToolbarAndGrid, Toolbar, GetIndexCell } from "../component";
import {
  BULK_ACTIONS,
  REASSIGN_TYPE,
  PROCESS_BUTTONS
} from "../common/constant";

import SimpleFilterValues from "./simpleFilterValues.json";

import styles from "./myitemsandmytasks.css";

import MYTaksEmptyStateIcon from "./icons/MYTaksEmptyState.svg";

const ACTION_BUTTONS = [
  { Id: BULK_ACTIONS.SUBMIT, label: i18nMark("Approve") },
  { Id: BULK_ACTIONS.REASSIGN, label: i18nMark("Reassign") },
  { Id: BULK_ACTIONS.REJECT, label: i18nMark("Reject") }
];
const { showWarning } = Toast;

export function MyTasksWrapper({ uId, ...restProps }) {
  createGridDataStore(uId);
  const [flowModel, setFlowModel] = useState();

  useEffect(function getFlowModel() {
    let [getFlowModel] = ProcessService.getFlowModel(restProps.modelId);
    getFlowModel().then((flowModel) => {
      setFlowModel(flowModel);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return flowModel ? (
    <Store uId={uId}>
      <MyTasks {...restProps} flowSchema={flowModel} />
    </Store>
  ) : null;
}

export function MyTasks({
  className,
  activityId: activityIdProp,
  showTab = true,
  modelId: modelIdProp,
  onRowClick: onRowClickProp,
  flowModel,
  preventRedirection,
  flowSchema
}) {
  const { useStateStore: useGridStore } = useStore();

  const gridPageNumber = useGridStore((state) => state.gridPageNumber);
  const gridPageSize = useGridStore((state) => state.gridPageSize);
  const gridData = useGridStore((state) => state.gridData);
  let selectedRowsIds = useGridStore((state) => state.selectedRowsIds);

  const [formData, setFormData] = useState(null);
  const match = useRouteMatch();
  const history = useHistory();
  const modelId = modelIdProp || match.params.modelId;
  const [routeActivityId, setRouteActivityId] = useState(
    activityIdProp || match.params["activityId"]
  );
  const [showEmptyState, setShowEmptyState] = useState(false);

  let [myStepsTasksCount, setMyStepsTasksCount] = useState(null);
  let [selectedStep, setSelectedStep] = useState(null);
  let [stepWiseFields, setStepWiseFields] = useState({});
  let [bulkActionButtons, setBulkActionButtons] = useState(ACTION_BUTTONS);

  let searchText = useRef("");
  const selectedRows = useRef([]);
  const simpleFields = [
    {
      Id: "_created_by",
      IsSystemField: true,
      Name: "Created by",
      Type: "User"
    },
    {
      Id: "Name",
      IsSystemField: true,
      Name: "Item name",
      Type: "List"
    },
    {
      Id: "ActivityInstance.ExpectedAt",
      IsSystemField: false,
      Name: "Deadline",
      Type: "Date"
    }
  ];

  const accountId = useAccountStore.getState()._id;
  const currentUser = useUserStore((state) => state.currentUser);

  const unSubscribeBulkAction = useRef(() => {});
  const refreshTimerIdRef = useRef(null);

  const advanceFilterFieldData = useRef([]);

  useEffect(function historyListener() {
    const unlisten = history.listen((location) => {
      let url = `/process/${modelId}/mytasks`;
      if (location.pathname.includes(url)) {
        setFormData(null);
      }
    });
    return () => {
      unlisten();
    };
  }, []);

  useEffect(
    function bulkActionSubscribe() {
      unSubscribeBulkAction.current();
      unSubscribeBulkAction.current = useBulkActionsStore.subscribe(
        (state) => state.actionCompletedData,
        ({ successRowsCount, activityId, refreshData, action }) => {
          let newCount = 0;
          myStepsTasksCount.map((step) => {
            if (step._id === activityId) {
              newCount = Math.max(0, step.Count - successRowsCount);
              step.Count = newCount;
            }
            return step;
          });
          if (refreshData && action === BULK_ACTIONS.SUBMIT) {
            getMyStepsTasksCount(true);
          } else {
            setMyStepsTasksCount([...myStepsTasksCount]);
          }
          useGridStore.getState().setGridCount(newCount);
        }
      );

      return () => {
        unSubscribeBulkAction?.current();
        clearTimeout(refreshTimerIdRef.current);
      };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myStepsTasksCount]
  );

  useEffect(
    function updatebulkActionButtons() {
      flowModel && routeActivityId && getBulkActionButtons(routeActivityId);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [routeActivityId]
  );

  function getBulkActionButtons(activityId) {
    let actionButtons = ACTION_BUTTONS;
    let configActionButtons = [
      PROCESS_BUTTONS.REASSIGN,
      PROCESS_BUTTONS.REJECT
    ];
    if (activityId === "SendBackToInitiator") {
      actionButtons = actionButtons.filter((button) => {
        return button.Id !== BULK_ACTIONS.REASSIGN;
      });
    }
    let rootModel = flowModel.getModel();
    let buttonRow = rootModel.getList("Button", "Row")[0];
    let buttonRowList = buttonRow.getList("Row", "Column");

    buttonRowList.forEach((button) => {
      let actionType = button.getValue("Name");
      if (configActionButtons.includes(actionType)) {
        let buttonPermissionList = button.getList("Column", "Permission");
        if (!buttonPermissionList.isEmpty()) {
          let columnPermission = buttonPermissionList.filter((permission) => {
            return (
              permission.getValue("Activity") === activityId &&
              permission.getValue("Permission") === "Hidden"
            );
          });
          if (columnPermission.length) {
            actionButtons = actionButtons.filter((action) => {
              return action.label !== actionType;
            });
          }
        }
      }
    });
    setBulkActionButtons(actionButtons);
  }

  function getQueueData(
    pageNum,
    selectedStepId,
    resetData,
    isBulkReassigned,
    pageSize,
    myTasks
  ) {
    const promise = ProcessService.getPendingTasks({
      activityId: selectedStepId || selectedStep._id,
      currentPage: pageNum || gridPageNumber,
      rowsPerPage: pageSize || gridPageSize,
      q: searchText.current,
      modelId
    });
    promise
      .onResponse((data) => {
        useGridStore.getState().setGridLoading(false);
        if (resetData) {
          useGridStore.getState().reset();
        }
        const { gridData } = useGridStore.getState();
        data.Data.forEach((row, index) => {
          row.showCheckbox = true;
          if (isBulkReassigned) {
            const oldRow = gridData.Data.find(
              (oldRow) => oldRow._id === row._id
            );
            if (oldRow && oldRow.showError) {
              row.showCheckbox = oldRow.showCheckbox;
              row.errorMessage = oldRow.errorMessage;
              row.showError = oldRow.showError;
            }
          }
        });
        useGridStore.getState().setGridPageSize(data.PageSize || gridPageSize);

        useGridStore.getState().setGridData(data);
        const count = searchText.current?.length
          ? data.Data.length
          : getTasksCount(
              myTasks || myStepsTasksCount,
              selectedStepId || selectedStep._id
            );
        if (count >= 0) {
          useGridStore.getState().setGridCount(count);
        }
      })
      .onError((err) => {
        useGridStore.getState().setGridLoading(false);
      });
  }
  useEffect(function getStepCount() {
    useGridStore.getState().reset();
    getMyStepsTasksCount(false);
    return () => {
      useGridStore.getState().reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function getMyStepsTasksCount(isBulkReassigned, resetData = false) {
    const [promise] = ProcessService.getPendingTasksCount({ modelId });
    promise()
      .then((myTasks) => {
        useProcessStore.getState().setMytaskDetails(myTasks, modelId, true);
        if (myTasks && myTasks.length) {
          const previousActivity = myTasks.find(
            (activity) => activity._id === routeActivityId
          );
          let selectedStepId;
          if (activityIdProp) {
            let activityFromProp = myTasks.find(
              (activity) => activity._id === activityIdProp
            );
            if (activityFromProp) {
              selectedStepId = activityIdProp;
            } else {
              setShowEmptyState(true);
              return;
            }
          } else if (previousActivity) {
            selectedStepId = routeActivityId;
          } else {
            selectedStepId = myTasks[0]._id;
          }
          getQueueData(
            resetData ? 1 : gridPageNumber,
            selectedStepId,
            resetData,
            isBulkReassigned,
            gridPageSize,
            myTasks
          );
          getStepFields(selectedStepId);
          setRouteActivityId(selectedStepId);
          if (!preventRedirection) {
            window.history.replaceState(
              null,
              document.title,
              `/view/process/${modelId}/mytasks/${selectedStepId}`
            );
          }
          setMyStepsTasksCount(myTasks);
          useGridStore
            .getState()
            .setGridCount(getTasksCount(myTasks, selectedStepId));
          setSelectedStep(previousActivity || myTasks[0]);
        } else {
          setShowEmptyState(true);
          if (routeActivityId && !preventRedirection) {
            window.history.replaceState(
              null,
              document.title,
              `/view/process/${modelId}/mytasks`
            );
          }
        }
      })
      .catch((err) => {
        console.error("failed", err);
      });
  }

  function getStepData(stepId) {
    useGridStore.getState().reset();
    searchText.current = "";
    const step = myStepsTasksCount.find((step) => step._id === stepId);
    setSelectedStep(step);
    setRouteActivityId(step._id);
    if (!preventRedirection) {
      window.history.replaceState(
        null,
        document.title,
        `/view/process/${modelId}/mytasks/${step._id}`
      );
    }
    useGridStore.getState().setGridPageNumber(1);
    useGridStore.getState().setGridLoading(true);
    getQueueData(1, stepId, true);
    if (!stepWiseFields[stepId]) {
      getStepFields(stepId);
    }
  }
  function getStepFields(stepId) {
    const promise = ProcessService.getStepFieldList(modelId, stepId);
    promise
      .onResponse((fields) => {
        const deadLineField = {
          Id: "ActivityInstance.ExpectedAt",
          IsSystemField: true,
          Model: modelId,
          Name: "Deadline",
          ReadOnly: true,
          Type: "DateTime",
          Widget: null
        };
        advanceFilterFieldData.current = [...fields, deadLineField];
        stepWiseFields[stepId] = fields;
        setStepWiseFields({ ...stepWiseFields });
      })
      .onError((err) => console.error("failed", err));
  }
  function onItemclick(row) {
    if (onRowClickProp) {
      onRowClickProp(row);
    } else {
      let url = `/process/${modelId}/form/${row._id}/${row._activity_instance_id}`;
      setFormData({
        _id: row._id,
        _activity_instance_id: row._activity_instance_id,
        _flow_id: modelId
      });
      window.history.pushState("", "", url);
    }
  }
  function getTasksCount(mytasks, stepId) {
    if (mytasks) {
      const step = mytasks.find((step) => step._id === stepId);
      return step.Count;
    }
  }

  function onSearch(value, selectedStep) {
    searchText.current = value;
    getQueueData(gridPageNumber, selectedStep?._id);
  }

  const delayedSearch = useCallback(
    debounce(function (value) {
      onSearch(value, selectedStep);
    }, 300),
    [selectedStep]
  );

  const onPreferenceUpdate = ({ config, pageNumber, dontCallGetApi }) => {
    const payload = {
      AppId: modelId,
      ConfigJson: config,
      ViewId: selectedStep && selectedStep._id,
      ViewType: "WorkflowStep"
    };
    let [savePreference] = ProcessService.updateUserPreference(payload);
    savePreference()
      .then(() => {
        !dontCallGetApi &&
          getQueueData(
            pageNumber,
            selectedStep._id,
            false,
            false,
            config.PageSize
          );
      })
      .catch((err) => console.error("Failed to get user preference", err));
  };
  const onFilterChange = (filterData, defaultFilterType) => {
    useGridStore.getState().setGridPageNumber(1);
    useGridStore.getState().setGridLoading(true);
    const ConfigJson = {
      Columns: gridData.Columns,
      Filter: filterData,
      FilterType: defaultFilterType,
      PageSize: gridPageSize,
      Sort: gridData.Sort
    };
    onPreferenceUpdate({ config: ConfigJson, pageNumber: 1 });
  };
  const onRowSelect = (rowIds, rows) => {
    if (!rowIds.length) {
      useGridStore.getState().setSelectedRowsIds([]);
    } else {
      let noErrorIds = [];
      let noErrorRows = [];
      rows.forEach((row) => {
        if (!row.showError) {
          noErrorRows.push(row);
          noErrorIds.push(row._id);
        }
      });
      selectedRows.current = noErrorRows;
      useGridStore.getState().setSelectedRowsIds(noErrorIds);
    }
  };
  const onErrorHover = (hoveredRow) => {
    hoveredRow.showCheckbox = true;
    hoveredRow.showError = false;
    hoveredRow.showSpinner = false;
    hoveredRow.rowDisabled = false;
    hoveredRow.errorMessage = null;
    hoveredRow.action = null;
    if (!refreshTimerIdRef.current) {
      refreshTimerIdRef.current = setTimeout(() => {
        let { gridData } = useGridStore.getState();
        useGridStore.getState().setGridData({ ...gridData });
        refreshTimerIdRef.current = null;
      }, 5000);
    }
  };
  const rowSelection = useMemo(
    function selectRow() {
      const { gridPageNumber, gridPageSize } = useGridStore.getState();
      return GetIndexCell(
        onRowSelect,
        selectedRowsIds,
        gridPageNumber,
        gridPageSize,
        onErrorHover
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedRowsIds]
  );

  const resetGridRows = () => {
    const { gridData } = useGridStore.getState();
    const resetRows = gridData.Data.map((row) => {
      row.showCheckbox = true;
      row.showError = false;
      row.showSpinner = false;
      row.rowDisabled = false;
      row.action = null;
      return row;
    });
    useGridStore.getState().setGridData({ ...gridData, Data: resetRows });
  };

  const onBulkApprove = () => {
    let requestData = [];
    selectedRows.current.forEach((row) => {
      if (!row.showError) {
        requestData.push({
          _id: row._id,
          _activity_instance_id: row._activity_instance_id,
          activityId: routeActivityId
        });
        row.showSpinner = true;
        row.showCheckbox = false;
        row.rowDisabled = true;
      }
      row.action = BULK_ACTIONS.SUBMIT;
    });

    const [promise] = ProcessService.bulkSubmit(modelId, {
      Values: requestData,
      body_params: { action: BULK_ACTIONS.SUBMIT }
    });
    promise().then(
      (response) => {
        let validationFailedRowsIds = [];
        let validationPassedRows = [];
        response &&
          response.forEach((rowInfo) => {
            if (rowInfo.Status === "Fail") {
              validationFailedRowsIds.push(rowInfo._id);
            } else {
              validationPassedRows.push(rowInfo);
            }
          });
        if (validationFailedRowsIds.length) {
          selectedRows.current.forEach((row) => {
            if (validationFailedRowsIds.includes(row._id)) {
              row.showError = true;
              row.showSpinner = false;
              row.rowDisabled = false;
            }
          });
        }
        if (validationPassedRows.length) {
          useBulkActionsStore.getState().setSubmitActionPerformedRows({
            ...useBulkActionsStore.getState().submitActionPerformedRows,
            [routeActivityId]: [
              ...useBulkActionsStore
                .getState()
                .getStepWiseSubmitActionPerformedRows(routeActivityId),
              ...validationPassedRows
            ]
          });
        } else {
          showWarning({
            message: `${validationFailedRowsIds.length} item(s) awaiting your inputs`,
            duration: 0
          });
        }
        selectedRows.current = [];
        useGridStore.getState().setSelectedRowsIds([]);
        useGridStore.getState().setGridData({
          ...gridData,
          Data: gridData.Data.slice()
        });
      },
      (err) => {
        resetGridRows();
        console.error(err);
      }
    );
  };
  const onBulkReject = (comments) => {
    let data = [];
    selectedRows.current.forEach((row) => {
      if (!row.showError) {
        row.showSpinner = true;
        row.showCheckbox = false;
        row.rowDisabled = true;
        data.push({
          _id: row._id,
          _activity_instance_id: row._activity_instance_id,
          activityId: routeActivityId
        });
      }
      row.action = BULK_ACTIONS.REJECT;
    });
    const [promise] = ProcessService.bulkReject(modelId, {
      Values: data,
      body_params: { Note: comments, action: BULK_ACTIONS.REJECT }
    });
    promise().then(
      (response) => {
        let rows = gridData.Data.slice();
        let validationPassedRows = [];
        response.forEach((rejectedRow) => {
          if (rejectedRow.Status === "Success") {
            // const rejectedRowIndex = rows.findIndex(
            //   (row) => row._id === rejectedRow._id
            // );
            validationPassedRows.push(rejectedRow);
            // rows.splice(rejectedRowIndex, 1);
          } else {
            let failedRow = rows.find((row) => row._id === rejectedRow._id);
            failedRow.showError = true;
            failedRow.showSpinner = false;
            failedRow.rowDisabled = false;
          }
        });
        useBulkActionsStore.getState().setRejectActionPerformedRows({
          ...useBulkActionsStore.getState().approveActionPerformedRows,
          [routeActivityId]: [
            ...useBulkActionsStore
              .getState()
              .getStepWiseRejectActionPerformedRows(routeActivityId),
            ...validationPassedRows
          ]
        });

        selectedRows.current = [];
        useGridStore.getState().setGridData({ ...gridData, Data: rows });
        useGridStore.getState().setSelectedRowsIds([]);
      },
      (err) => {
        resetGridRows();
        console.error(err);
      }
    );
  };
  const onBulkReassign = (reaasignData) => {
    let data = [];
    selectedRows.current.forEach((row) => {
      if (!row.showError) {
        row.showSpinner = true;
        row.rowDisabled = true;
        row.showCheckbox = false;
        if (
          row._current_assigned_to?.find(
            (user) => reaasignData.ReassignTo._id === user._id
          )
        ) {
          row.showError = true;
          row.rowDisabled = false;
          row.showSpinner = false;
          row.errorMessage = i18n._(
            t`The item is already assigned to  ${reaasignData.ReassignTo.Name}`
          );
        } else {
          data.push({
            _id: row._id,
            _activity_instance_id: row._activity_instance_id,
            _activity_id: routeActivityId
          });
        }
      }
      row.action = BULK_ACTIONS.REASSIGN;
    });
    const [promise] = ProcessService.bulkReassign(modelId, {
      Values: data,
      body_params: {
        _activity_id: routeActivityId,
        ReassignedFrom: [
          {
            _id: currentUser._id,
            Name: currentUser.Name,
            Kind: "User"
          }
        ],
        ReassignType: REASSIGN_TYPE.APPROVER,
        action: BULK_ACTIONS.REASSIGN,
        ...reaasignData
      }
    });
    promise().then(
      (response) => {
        // let rows = gridData.Data.slice();
        useBulkActionsStore.getState().setReassignActionPerformedRows({
          ...useBulkActionsStore.getState().reassignActionPerformedRows,
          [routeActivityId]: [
            ...useBulkActionsStore
              .getState()
              .getStepWiseReassignActionPerformedRows(routeActivityId),
            ...data
          ]
        });

        selectedRows.current = [];
        useGridStore
          .getState()
          .setGridData({ ...gridData, Data: gridData.Data.slice() });
        useGridStore.getState().setSelectedRowsIds([]);
      },
      (err) => {
        resetGridRows();
        console.error(err);
      }
    );
  };

  const onCloseModal = (event) => {
    let _selectedStep = selectedStep?._id;
    const url = `/process/${modelId}/mytasks/${_selectedStep}`;
    setFormData(null);
    window.history.pushState("", "", url);
    if (event !== null) {
      useGridStore.getState().reset();
      getMyStepsTasksCount(false, true);
    }
  };

  return (
    <ErrorBoundary>
      <Fragment>
        {showEmptyState && !myStepsTasksCount && (
          <div className={styles.emptyStateContainer}>
            <Icon paddingNone={true}>
              <MYTaksEmptyStateIcon />
            </Icon>
            <div className={`h4600 ${styles.emptyStateText}`}>
              <div>
                <Trans>No tasks assigned</Trans>
              </div>
              <div className="base">
                <Trans>
                  Check back later to see if there&apos;s anything pending
                </Trans>
              </div>
            </div>
          </div>
        )}
        {myStepsTasksCount && (
          <div
            className={`${styles.myItemsContainer} ${className} ${
              myStepsTasksCount && myStepsTasksCount.length === 1
                ? styles.myItemsContainerWithoutTab
                : null
            }`}
          >
            <Fragment>
              {showTab && myStepsTasksCount.length !== 1 && (
                <div className={styles.itemsCountContainer}>
                  <SecondaryTabs
                    activeTabId={selectedStep && selectedStep._id}
                    onChange={getStepData}
                    maxVisible={3}
                    size={SECONDARY_TAB_SIZES.SMALL}
                  >
                    {myStepsTasksCount.map((stepTasks) => (
                      <SecondaryTab key={stepTasks._id} tabId={stepTasks._id}>
                        <div className={styles.taskTabHolder}>
                          <span className={"base600"}>{stepTasks.Name}</span>
                          <div className={`${styles.stepName} ellipsis`}>
                            {stepTasks.StepName}
                          </div>
                          {stepTasks.Count ? (
                            <div className={`${styles.countChip} small600`}>
                              {stepTasks.Count}
                            </div>
                          ) : null}
                        </div>
                      </SecondaryTab>
                    ))}
                  </SecondaryTabs>
                </div>
              )}
              {gridData.Columns && (
                <div className={styles.container}>
                  <ToolbarAndGrid>
                    <ToolbarAndGrid.Toolbar>
                      <Toolbar
                        title={selectedStep && selectedStep.StepName}
                        fields={
                          stepWiseFields[selectedStep && selectedStep._id]
                        }
                        selectedFields={gridData.Columns}
                        onSearch={delayedSearch}
                        selectedRowsIds={selectedRowsIds}
                        onPreferenceUpdate={onPreferenceUpdate}
                        onBulkApprove={onBulkApprove}
                        onBulkReject={onBulkReject}
                        onBulkReassign={onBulkReassign}
                        actionButtons={bulkActionButtons}
                        searchValue={searchText.current}
                      />
                    </ToolbarAndGrid.Toolbar>
                    <ToolbarAndGrid.Grid>
                      <Grid
                        onPreferenceUpdate={onPreferenceUpdate}
                        getGriData={getQueueData}
                        onRowClick={onItemclick}
                        rowSelection={rowSelection}
                        key="mytasks"
                        flowSchema={flowSchema}
                        showRowSelection={true}
                      />
                    </ToolbarAndGrid.Grid>
                    <ToolbarAndGrid.Filter>
                      <Filter
                        listData={{
                          FilterType: gridData.FilterType,
                          Filter: gridData.Filter
                        }}
                        type="Both"
                        advanceFilterFieldData={advanceFilterFieldData.current}
                        simpleFilterFieldData={simpleFields}
                        onFilterChange={onFilterChange}
                        baseUrl={`/process/2/${accountId}/${modelId}/pending/${
                          selectedStep && selectedStep._id
                        }`}
                        onCloseFilter={() => {
                          useGridStore.getState().setShowFilter(false);
                        }}
                        simpleFilterRelativeValue={SimpleFilterValues}
                      />
                    </ToolbarAndGrid.Filter>
                  </ToolbarAndGrid>
                </div>
              )}
            </Fragment>
          </div>
        )}
        {formData && (
          <LazyLoadComponent
            component={() =>
              import(/*webpackPreload:true*/ "../form/formmodal")
            }
            componentName={"FormModalWrapper"}
            instanceId={formData._id}
            activityInstanceId={formData._activity_instance_id}
            storeId={formData._id}
            modelId={formData._flow_id}
            viewType="Modal"
            onModalClose={onCloseModal}
          />
        )}
      </Fragment>
    </ErrorBoundary>
  );
}
MyTasks.propTypes = {
  activityId: PropTypes.string,
  showTab: PropTypes.bool,
  modelId: PropTypes.string,
  onRowClick: PropTypes.func,
  flowModel: PropTypes.object,
  preventRedirection: PropTypes.bool,
  className: PropTypes.string,
  flowSchema: PropTypes.object
};

MyTasksWrapper.propTypes = {
  uId: PropTypes.string
};
